import React, { ChangeEvent, useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import InspectionRepair from "./components/InspectionRepair";
import CustomerProfile from "./components/CustomerProfile";
import customFetch from "../../API/CustomFetch";
import {
  customer_consent_valid_url,
  customer_consent_verify_otp,
} from "../../API/api";
import showToastMessage from "../../Toastify/Index";
import ErrorFallback from "../ErrorFallback/ErrorFallback";
import { ClipLoader } from "react-spinners";
import TermsAndConditionsModal from "./components/TermsAndConditionsModal";

const CustomerConsent = () => {
  const [validUrl, setValidUrl] = useState<Boolean>(false);
  const [otp, setOtp] = useState("");
  const [OTPVerified, SetOTPVerified] = useState(false);
  const [formType, setFormType] = useState("");
  const [error, setError] = useState<"unauthorized" | "none">("none");
  const [inspectionLoaded, setInspectionLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [typeMasterId, setTypeMasterId] = useState<string | null>(null);

  // new states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeId = urlParams.get("type");
    const vehicleProfileId = urlParams.get("vp");

    setTypeMasterId(typeId); // <-- store in state

    if (typeId && parseInt(typeId) === 53) {
      setFormType("Inspection Information");
    } else if (typeId && parseInt(typeId) === 54) {
      setFormType("Repair Criteria Information");
    } else if (typeId && parseInt(typeId) === 80) {
      setFormType("Final Inspection Report");
    }

    const url = `${customer_consent_valid_url}/${typeId}/${vehicleProfileId}`;
    customFetch(url)
      .then((res) => {
        if (res.SUCCESS) setValidUrl(true);
        else {
          setValidUrl(false);
          setError("unauthorized");
        }
      })
      .catch(() => {
        setValidUrl(false);
        setError("unauthorized");
      });
  }, []);

  const handleOTP = (e: ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
  };

  const handleSubmit = () => {
    if (!otp.trim()) {
      showToastMessage(false, "Please enter the OTP you received on WhatsApp");
      return;
    }

    setIsLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const typeMasterId = urlParams.get("type");
    const vehicleProfileId = urlParams.get("vp");
    const url = `${customer_consent_verify_otp}/${typeMasterId}/${vehicleProfileId}/${otp}`;
    customFetch(url, "", { method: "POST" })
      .then((res) => {
        if (res?.SUCCESS) {
          showToastMessage(true, "OTP verification successful");
          SetOTPVerified(true);
        } else if (res?.SUCCESS === false) {
          showToastMessage(false, res?.MESSAGE);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleCheckboxChange = () => {
    setIsCheckboxChecked(!isCheckboxChecked);
    setIsModalOpen(true);
  };

  const handleAccept = () => {
    setIsAccepted(true);
    setIsModalOpen(false);
    showToastMessage(true, "You have accepted the Terms & Conditions");
  };

  const handleReject = () => {
    setIsAccepted(false);
    setIsModalOpen(false);
    setIsCheckboxChecked(false);
    showToastMessage(false, "You have rejected the Terms & Conditions");
  };

  useEffect(() => {
    const beforePrint = () => {
      const otpInput = document.getElementById("otp-input");
      if (otpInput) otpInput.style.display = "none";
    };
    const afterPrint = () => {
      const otpInput = document.getElementById("otp-input");
      if (otpInput) otpInput.style.display = "block";
    };
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  return (
    <div className="px-2 md:px-6 my-4" id="pdf-content">
      {error === "unauthorized" ? (
        <>
          <Navbar />
          <ErrorFallback message="Unauthorized or Invalid Link" />
        </>
      ) : (
        <div className="my-4 mx-2 md:mx-4">
          <Navbar />
          <h1 className="text-2xl font-bold mb-4 text-center">
            Customer Consent Form - {formType}
          </h1>{" "}
          <h1 className="text-2xl font-bold mb-4 text-center">
            Customer Consent Form - {formType}
          </h1>
          <CustomerProfile typeMasterId={typeMasterId} />
          <InspectionRepair onDataLoaded={setInspectionLoaded} />
          {inspectionLoaded && !OTPVerified && (
            <>
              {/* ✅ Checkbox Section */}
              <div className="flex items-center justify-center my-4">
                <input
                  id="termsCheckbox"
                  type="checkbox"
                  checked={isCheckboxChecked}
                  onChange={handleCheckboxChange}
                  className="mr-2 w-5 h-5"
                />
                <label
                  htmlFor="termsCheckbox"
                  className="text-sm text-gray-700"
                >
                  I agree to the Terms & Conditions
                </label>
              </div>

              {/* ✅ OTP Box */}
              <div className="text-center p-4 border-2" id="otp-input">
                <input
                  className="border border-black px-2 py-2 text-center"
                  type="text"
                  value={otp}
                  onChange={handleOTP}
                  placeholder="Enter OTP"
                  disabled={!isAccepted} // disable until accepted
                />
                {!isAccepted && (
                  <p className="text-red-500 text-sm italic mt-2">
                    Please accept Terms & Conditions before entering OTP
                  </p>
                )}
              </div>

              <div className="text-center">
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 mt-8 rounded disabled:opacity-50"
                  onClick={handleSubmit}
                  disabled={isLoading || !isAccepted}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <ClipLoader
                        color="#ffffff"
                        size={20}
                        cssOverride={{ marginRight: "8px" }}
                      />
                      Verifying...
                    </span>
                  ) : (
                    "Verify OTP to Agree"
                  )}
                </button>
              </div>
            </>
          )}
          <div className="text-center">
            {OTPVerified && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 mt-8 rounded"
                onClick={window.print}
              >
                Print
              </button>
            )}
          </div>
          <TermsAndConditionsModal
            isOpen={isModalOpen}
            isAccepted={isAccepted}
            onAccept={handleAccept}
            onReject={handleReject}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerConsent;
